import { Box, Typography } from "@mui/material";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { notificationAdd } from "../../actions/notification";
import * as reportsApi from "../../api/reports";
import * as calFns from "../../components/Calendar/functions/calendarFunctions";
import List, { EmptyView, ListActions, Menu, MenuItem } from "../../components/OList";
import { t, tl } from "../../components/translate";
import { downloadExcel } from "../../helpers/files";
import Can from "../Policy/Can";
import { formatDataForExcel, formatHeaderForExcel } from "../accounts/Reports/helper";
import Filters from "./Filters";
import "./Reports.scss";
import { getCustomerNumber } from "../Client/ClientList";
import { BranchReportProps } from "./SalesByServicesReport";
import { RootState } from "../../store";
import useIsReactNativeWebView from "../../hooks/useIsReactNativeWebView";
import useMobileScreen from "../../hooks/useMobileScreen";
import { rupeeDisplay } from "../../helpers/rupee";

const or = (val) => val || "";

const docColumns = (isBranchReport) => [
  ...(isBranchReport ? [t("reports.resourceCentreName")] : []),
  t("reports.customerNumber"),
  t("reports.client"),
  t("reports.clientFirstName"),
  t("reports.clientLastName"),
  t("reports.billedBy"),
  t("reports.services"),
  t("reports.billNumber"),
  t("reports.billingDateBS"),
  t("reports.billingDateAD"),
  t("reports.taxableAmount"),
  t("reports.taxAmount"),
  t("reports.totalAmount"),
  t("reports.cumPayment"),
  t("reports.ageingDays"),
  t("reports.receivableDue")
];

interface DocRowInterface {
  resourceCentreName: string;
  customerNumber: string;
  clientFirstName: string;
  clientLastName: string;
  clientName: string;
  enteredBy: string;
  services: string[];
  billInfo: { [key: string]: string };
  billingDate: Date;
  taxableAmount: number;
  taxAmount: number;
  totalAmount: number;
  cumPayment: number;
  ageingDays: number;
  receivableDue: number;
  // eslint-disable-next-line camelcase
  __meta__row_type: string;
}

const makeDocRowProcessor =
  (isBranchReport) =>
  ({
    customerNumber,
    clientFirstName,
    clientLastName,
    clientName,
    services,
    billInfo,
    billingDate,
    taxableAmount,
    taxAmount,
    totalAmount,
    cumPayment,
    ageingDays,
    receivableDue,
    enteredBy,
    resourceCentreName,
    // eslint-disable-next-line camelcase
    __meta__row_type
  }: DocRowInterface) => {
    // eslint-disable-next-line camelcase
    if (__meta__row_type === "segment_summary") return null;
    return [
      ...(isBranchReport ? [resourceCentreName] : []),
      or(getCustomerNumber(customerNumber)),
      or(clientName),
      or(clientFirstName),
      or(clientLastName),
      or(enteredBy),
      services,
      billInfo.billNumber,
      calFns.bsShortDate(billingDate),
      calFns.adShortDate(billingDate),
      taxableAmount,
      taxAmount,
      totalAmount,
      cumPayment,
      ageingDays,
      receivableDue
    ];
  };

const UserWiseCollectionReport = ({
  isBranchReport = false
}: BranchReportProps): React.JSX.Element => {
  // custom hooks
  const dispatch = useDispatch();
  const isMobileScreen = useMobileScreen();
  const { isRnWebView } = useIsReactNativeWebView();

  // selectors
  const resourceCentreId = useSelector((state: RootState) => state.userContext.resourceCentre?.id);
  const resourceCentres = useSelector((state: RootState) => state.userBranch.collection) || [];

  // local states
  const [reportData, setReportData] = React.useState([]);
  const [isRequestLoading, setIsRequestLoading] = React.useState(false);
  const [filters, setFilters] = React.useState({
    from: calFns.startOfDay(new Date()).toDate(),
    until: new Date(),
    employeeIds: [],
    ...(isBranchReport ? { resourceCentreIds: resourceCentreId ? [resourceCentreId] : [] } : {})
  });

  const applyFilters = (newFilters) => {
    setFilters(newFilters);
  };

  // effects
  React.useEffect(() => {
    setIsRequestLoading(true);
    reportsApi
      .getUseWiseCollectionReport(filters)
      .then((data) => {
        setReportData(data);
      })
      .finally(() => {
        setIsRequestLoading(false);
      });
  }, [filters, resourceCentres]);

  return (
    <Can policyAccessKey="report:salesReport">
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* filter */}
        <Box sx={{ px: 3, py: 2 }}>
          <Filters
            filters={filters}
            onSetFilters={(newFilters) => applyFilters(newFilters)}
            hideFiscalYear
            isRequestLoading={isRequestLoading}
          />
        </Box>

        {/* list */}
        <Box
          className="userWiseCollectionReport"
          sx={{
            borderTop: 1,
            borderColor: "divider",
            width: "100%",
            overflow: "auto"
          }}
        >
          <Box sx={{ minWidth: isMobileScreen || isRnWebView ? "900px" : "auto", mb: "40px" }}>
            <List
              data={reportData}
              rowHeight={50}
              defaultSortColumn="referrers"
              defaultSortOrder={-1}
              activeRow={1}
              adjustHeightToContents
              columns={[
                ...(isBranchReport
                  ? [
                      {
                        key: "resourceCentre",
                        label: "Branch",
                        formatter: ({ resourceCentreName }) => (
                          <Typography>{resourceCentreName}</Typography>
                        ),
                        sortable: true,
                        segmentable: true,
                        segmentBy: (row) => row.resourceCentreName,
                        sortFunction: (a, b) =>
                          or(a.resourceCentreName).toLowerCase() >
                          or(b.resourceCentreName).toLowerCase()
                            ? 1
                            : -1
                      }
                    ]
                  : []),
                {
                  key: "collectedBy",
                  label: "Collected by",
                  sortable: true,
                  formatter: ({ collectedBy }) => <Typography>{collectedBy || ""}</Typography>
                },
                {
                  key: "cash",
                  label: "cash",
                  formatter: ({ cash }) => <Typography>{cash || ""}</Typography>
                },
                {
                  key: "card",
                  label: "card",
                  formatter: ({ card }) => <Typography>{card || ""}</Typography>
                },
                {
                  key: "fonePay",
                  label: "Fone Pay",
                  formatter: ({ fonePay }) => <Typography>{fonePay || ""}</Typography>
                },

                {
                  key: "totalAmount",
                  label: "Total Amt",
                  formatter: ({ totalAmount }) => <Typography>{totalAmount || ""}</Typography>
                }
              ]}
            >
              <EmptyView>
                <Box textAlign="center" padding="50px">
                  No items to show.
                </Box>
              </EmptyView>
              <ListActions>
                {({ getProcessedData }) => (
                  <Menu>
                    <MenuItem
                      onClick={async () => {
                        try {
                          await downloadExcel(
                            t("reports.dueReport", "en"),
                            formatHeaderForExcel(docColumns(isBranchReport)),
                            formatDataForExcel(
                              getProcessedData(),
                              makeDocRowProcessor(isBranchReport)
                            )
                          );
                        } catch (err) {
                          dispatch(
                            notificationAdd({
                              id: new Date().getUTCMilliseconds(),
                              variant: "error",
                              message: "Failed to download Excel.",
                              autoTimeout: true
                            })
                          );
                        }
                      }}
                    >
                      {tl("reports.excel")}
                    </MenuItem>
                  </Menu>
                )}
              </ListActions>
            </List>

            <Box className="totalBar">
              <Typography className="total-bar-title">{t("reports.total")}</Typography>
              <Typography className="total-bar-cash">
                {rupeeDisplay(
                  Number.parseFloat(
                    reportData.reduce((acc, cur) => acc + cur.cash || 0, 0)
                  ).toFixed(2) || 0
                )}
              </Typography>
              <Typography className="total-bar-card">
                {rupeeDisplay(
                  Number.parseFloat(
                    reportData.reduce((acc, cur) => acc + cur.card || 0, 0)
                  ).toFixed(2) || 0
                )}
              </Typography>
              <Typography className="total-bar-fonePay">
                {rupeeDisplay(
                  Number.parseFloat(
                    reportData.reduce((acc, cur) => acc + cur.fonePay || 0, 0)
                  ).toFixed(2) || 0
                )}
              </Typography>
              <Typography className="total-bar-totalAmount">
                {rupeeDisplay(
                  Number.parseFloat(
                    reportData.reduce((acc, cur) => acc + cur.totalAmount || 0, 0)
                  ).toFixed(2) || 0
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Can>
  );
};

export default UserWiseCollectionReport;
